<template>
  <div class="appointment-wizard">
    <div class="appointment-wizard__header">
      <Button
        v-if="!activationToken"
        icon="pi pi-arrow-left"
        class="p-button-text p-button-sm mb-3"
        label="Voltar aos agendamentos"
        @click="$router.push({ name: 'appointments' })"
      />
      <h1 class="appointment-wizard__page-title">Novo Agendamento</h1>

      <div class="appointment-wizard__stepper">
        <div
          v-for="(step, index) in wizardSteps"
          :key="step.key"
          class="wizard-step"
          :class="{
            'wizard-step--active': currentStep === index + 1,
            'wizard-step--done': currentStep > index + 1,
          }"
        >
          <div class="wizard-step__circle">
            <i v-if="currentStep > index + 1" class="pi pi-check" />
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span class="wizard-step__label">{{ step.label }}</span>
        </div>
      </div>
    </div>

    <div class="appointment-wizard__body">
      <AppointmentStepCountry
        v-if="currentStepKey === 'country'"
        :residence-country="residenceCountry"
        :selected-iso="wizardData.catalogCountry"
        @select="onCatalogCountrySelected"
      />

      <AppointmentStepService
        v-if="wizardData.catalogCountry || activationToken"
        v-show="currentStepKey === 'service'"
        :is-from-activation="!!activationToken"
        :catalog-country="wizardData.catalogCountry"
        :country="country"
        @select="onServiceSelected"
        @services-loaded="onServicesLoaded"
      />

      <AppointmentStepMeetingType
        v-if="currentStepKey === 'meetingType'"
        v-model="wizardData.meetingTypeChoice"
        :service="wizardData.selectedService"
        :office-options="presencialOfficeOptionsForUi"
        :selected-office-id="wizardData.presencialOfficeId"
        @valid="stepValid = $event"
        @office-selected="onMeetingTypeOfficeSelected"
      />

      <AppointmentStepDateTime
        v-if="currentStepKey === 'datetime' && wizardData.selectedService"
        v-model="wizardData.dateTime"
        :service="wizardData.selectedService"
        :appointment-mode="effectiveAppointmentMode"
        :office-id="officeIdForAvailability"
        :office-options="presencialOfficeOptionsForUi"
        :block-presencial-without-office="blockPresencialNoOffices"
        :needs-presencial-office-choice="needsPresencialOfficeChoice"
        :office-gate-message="presencialOfficeGateMessage"
        :is-from-activation="!!activationToken"
        @valid="stepValid = $event"
        @office-change="onPresencialOfficeChange"
      />

      <AppointmentStepConfirm
        v-if="currentStepKey === 'confirm'"
        v-model:notes="wizardData.notes"
        v-model:payment-method="wizardData.paymentMethod"
        :service="wizardData.selectedService"
        :date-time="wizardData.dateTime"
        :appointment-mode="effectiveAppointmentMode"
        :client-name="clientName"
        @valid="stepValid = $event"
      />
      <div
        v-if="currentStepKey === 'confirm' && existingAppointmentInfo"
        class="appointment-wizard__existing-alert"
      >
        <h4 class="appointment-wizard__existing-title">
          Já existe um agendamento para o mesmo serviço, edite o agendamento
          existente para alterar a data do mesmo.
        </h4>
        <div class="appointment-wizard__existing-grid">
          <div class="appointment-wizard__existing-row">
            <span class="appointment-wizard__existing-label">Data / Hora</span>
            <strong>{{ existingAppointmentInfo.dateTimeLabel }}</strong>
          </div>
          <div class="appointment-wizard__existing-row">
            <span class="appointment-wizard__existing-label">Consultor</span>
            <strong>{{ existingAppointmentInfo.consultantLabel }}</strong>
          </div>
          <div class="appointment-wizard__existing-row">
            <span class="appointment-wizard__existing-label"
              >Tipo de agendamento</span
            >
            <strong>{{ existingAppointmentInfo.typeLabel }}</strong>
          </div>
        </div>
      </div>

      <AppointmentStepSuccess
        v-if="currentStepKey === 'success'"
        :service="wizardData.selectedService"
        :date-time="wizardData.dateTime"
        :appointment-mode="effectiveAppointmentMode"
        :payment-method="wizardData.paymentMethod"
        :payment-link="wizardData.paymentLink"
        :is-from-activation="!!activationToken"
      />
    </div>

    <div
      v-if="currentStep > 1 && currentStepKey !== 'success'"
      class="appointment-wizard__footer"
    >
      <Button
        v-if="currentStep > 1"
        label="Anterior"
        icon="pi pi-arrow-left"
        class="p-button-outlined"
        :disabled="isSubmitting || (!!activationToken && currentStep === 2)"
        @click="prevStep"
      />
      <Button
        v-if="currentStepKey === 'meetingType' || currentStepKey === 'datetime'"
        label="Seguinte"
        icon="pi pi-arrow-right"
        icon-pos="right"
        :disabled="!stepValid"
        @click="nextStep"
      />
      <Button
        v-if="currentStepKey === 'confirm'"
        label="Confirmar Agendamento"
        icon="pi pi-check"
        icon-pos="right"
        :disabled="!stepValid || isSubmitting"
        :loading="isSubmitting"
        @click="submitAppointment"
      />
    </div>
  </div>
</template>

<script>
import {
  residenceCountryIso,
  normalizeCountryIso,
} from "@/utils/appointmentCountries";
import AppointmentStepCountry from "./AppointmentStepCountry.vue";
import AppointmentStepService from "./AppointmentStepService.vue";
import AppointmentStepMeetingType from "./AppointmentStepMeetingType.vue";
import AppointmentStepDateTime from "./AppointmentStepDateTime.vue";
import AppointmentStepConfirm from "./AppointmentStepConfirm.vue";
import AppointmentStepSuccess from "./AppointmentStepSuccess.vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export default {
  name: "AppointmentWizard",
  components: {
    AppointmentStepCountry,
    AppointmentStepService,
    AppointmentStepMeetingType,
    AppointmentStepDateTime,
    AppointmentStepConfirm,
    AppointmentStepSuccess,
  },
  props: {
    servicePriceId: {
      type: Number,
      default: null,
    },
    activationToken: {
      type: String,
      default: null,
    },
    clientName: {
      type: String,
      default: null,
    },
    country: {
      type: String,
      default: null,
    },
  },
  emits: ["success", "services-loaded"],
  data() {
    return {
      currentStep: 1,
      stepValid: false,
      isSubmitting: false,
      clientOfficeId: null,
      wizardData: {
        catalogCountry: null,
        selectedService: null,
        meetingTypeChoice: null,
        presencialOfficeId: null,
        dateTime: null,
        notes: "",
        paymentMethod: null,
        paymentLink: null,
      },
      existingAppointmentInfo: null,
    };
  },
  computed: {
    residenceCountry() {
      return residenceCountryIso(
        this.country,
        this.$root && this.$root.user ? this.$root.user : null
      );
    },
    needsCountryChoice() {
      const residence = this.residenceCountry;
      return !this.activationToken && !!residence && residence !== "PT";
    },
    wizardSteps() {
      const steps = [];
      if (this.needsCountryChoice) {
        steps.push({ key: "country", label: "País" });
      }
      steps.push({ key: "service", label: "Serviço" });
      if (
        this.wizardData.selectedService &&
        this.wizardData.selectedService.meetingType === "both"
      ) {
        steps.push({ key: "meetingType", label: "Modalidade" });
      }
      steps.push(
        { key: "datetime", label: "Data e hora" },
        { key: "confirm", label: "Confirmação" },
        { key: "success", label: "Concluído" }
      );
      return steps;
    },
    currentStepKey() {
      return this.wizardSteps[this.currentStep - 1]
        ? this.wizardSteps[this.currentStep - 1].key
        : "";
    },
    effectiveAppointmentMode() {
      const s = this.wizardData.selectedService;
      if (!s) return null;
      if (s.meetingType === "both") {
        return this.wizardData.meetingTypeChoice;
      }
      return s.meetingType;
    },
    officeIdForAvailability() {
      if (this.effectiveAppointmentMode !== "on-site") return null;
      return this.wizardData.presencialOfficeId != null
        ? Number(this.wizardData.presencialOfficeId)
        : null;
    },
    presencialOfficesAllowed() {
      const s = this.wizardData.selectedService;
      if (!s || !Array.isArray(s.offices)) return [];
      const catalog = normalizeCountryIso(this.wizardData.catalogCountry);
      return s.offices.filter(function (o) {
        if (!o.country) return true;
        if (!catalog) return true;
        return String(o.country).toUpperCase() === catalog;
      });
    },
    presencialOfficeOptionsForUi() {
      const residenceCountry = this.country
        ? this.country
        : this.$root && this.$root.user && this.$root.user.country
        ? String(this.$root.user.country).toUpperCase()
        : null;
      const sorted = [...this.presencialOfficesAllowed].sort(function (a, b) {
        const aCountry = String(a.country || "").toUpperCase();
        const bCountry = String(b.country || "").toUpperCase();
        const weight = function (country) {
          if (residenceCountry && country === residenceCountry) return 0;
          if (country === "PT") return 1;
          return 2;
        };
        const wa = weight(aCountry);
        const wb = weight(bCountry);
        if (wa !== wb) return wa - wb;
        if (aCountry !== bCountry) return aCountry.localeCompare(bCountry);
        return String(a.city || "").localeCompare(String(b.city || ""));
      });

      return sorted.map(function (o) {
        var city = o.city || "";
        var country = o.country || "";
        var label = city
          ? city + (country ? " (" + country + ")" : "")
          : String(o.id);
        return { id: Number(o.id), label: label };
      });
    },
    presencialOfficeGateMessage() {
      if (this.effectiveAppointmentMode !== "on-site") return "";
      var allowed = this.presencialOfficesAllowed;
      if (allowed.length === 0) {
        return "Não há balcões presenciais com agenda para este serviço na sua região. Pode optar por videoconferência (se disponível) ou contactar-nos.";
      }
      if (allowed.length > 1 && this.wizardData.presencialOfficeId == null) {
        return "Indique em que balcão quer ser atendido. O seu balcão de perfil pode não ser um dos locais onde este serviço é prestado pelos consultores.";
      }
      return "";
    },
    /** Sem nenhum balcão com agenda para o serviço na região */
    blockPresencialNoOffices() {
      if (this.effectiveAppointmentMode !== "on-site") return false;
      return this.presencialOfficesAllowed.length === 0;
    },
    /** Vários balcões: falta escolher qual (não pedir slots antes da escolha) */
    needsPresencialOfficeChoice() {
      if (this.effectiveAppointmentMode !== "on-site") return false;
      var allowed = this.presencialOfficesAllowed;
      return allowed.length > 1 && this.wizardData.presencialOfficeId == null;
    },
  },
  watch: {
    "wizardData.meetingTypeChoice"(next, prev) {
      if (prev != null && next != null && next !== prev) {
        this.wizardData.dateTime = null;
      }
      if (next === "on-site") {
        this.ensurePresencialOfficeSelection();
      }
    },
    clientOfficeId() {
      if (this.effectiveAppointmentMode === "on-site") {
        this.ensurePresencialOfficeSelection();
      }
    },
    presencialOfficesAllowed: {
      deep: true,
      handler() {
        if (this.effectiveAppointmentMode === "on-site") {
          this.ensurePresencialOfficeSelection();
        }
      },
    },
  },
  created() {
    this.loadClientOffice();
    if (!this.needsCountryChoice && !this.activationToken) {
      this.wizardData.catalogCountry = this.residenceCountry || "PT";
    }
  },
  methods: {
    clientCountriesForFilter() {
      const catalog = normalizeCountryIso(this.wizardData.catalogCountry);
      if (catalog) return [catalog];
      var country = this.residenceCountry;
      if (!country || country === "PT") return ["PT"];
      return [country, "PT"];
    },
    goToStep(key) {
      const index = this.wizardSteps.findIndex((step) => step.key === key);
      if (index >= 0) this.currentStep = index + 1;
    },
    onCatalogCountrySelected(iso) {
      const next = normalizeCountryIso(iso);
      if (this.wizardData.catalogCountry !== next) {
        this.wizardData.selectedService = null;
        this.wizardData.dateTime = null;
        this.wizardData.meetingTypeChoice = null;
        this.wizardData.presencialOfficeId = null;
        this.existingAppointmentInfo = null;
      }
      this.wizardData.catalogCountry = next;
      this.goToStep("service");
    },
    ensurePresencialOfficeSelection() {
      var allowed = this.presencialOfficesAllowed;
      if (allowed.length === 0) {
        this.wizardData.presencialOfficeId = null;
        return;
      }
      var current =
        this.wizardData.presencialOfficeId != null
          ? Number(this.wizardData.presencialOfficeId)
          : null;
      if (
        current != null &&
        allowed.some(function (o) {
          return Number(o.id) === current;
        })
      ) {
        return;
      }
      if (allowed.length === 1) {
        this.wizardData.presencialOfficeId = Number(allowed[0].id);
        return;
      }
      var cid =
        this.clientOfficeId != null ? Number(this.clientOfficeId) : null;
      if (
        cid != null &&
        allowed.some(function (o) {
          return Number(o.id) === cid;
        })
      ) {
        this.wizardData.presencialOfficeId = cid;
        return;
      }
      this.wizardData.presencialOfficeId = null;
    },
    onPresencialOfficeChange(officeId) {
      this.wizardData.presencialOfficeId =
        officeId != null ? Number(officeId) : null;
      this.wizardData.dateTime = null;
      this.existingAppointmentInfo = null;
    },
    onMeetingTypeOfficeSelected(officeId) {
      this.wizardData.presencialOfficeId =
        officeId != null ? Number(officeId) : null;
      this.wizardData.dateTime = null;
      this.existingAppointmentInfo = null;
    },
    async loadClientOffice() {
      if (!this.$root.user || !this.$root.user.isClient) {
        this.clientOfficeId = null;
        return;
      }
      try {
        const res = await this.$root.apiGet(this.$root.api.profile, {
          params: { dataType: "profile" },
        });
        const oid = res.data && res.data.data && res.data.data.officeId;
        this.clientOfficeId = oid != null && oid !== "" ? Number(oid) : null;
      } catch {
        this.clientOfficeId = null;
      }
    },
    async onServiceSelected(service) {
      await this.loadClientOffice();
      this.wizardData.selectedService = service;
      this.wizardData.dateTime = null;
      this.wizardData.paymentLink = null;
      this.wizardData.presencialOfficeId = null;
      if (service.meetingType === "both") {
        this.wizardData.meetingTypeChoice = null;
      } else {
        this.wizardData.meetingTypeChoice = service.meetingType;
      }
      if (this.effectiveAppointmentMode === "on-site") {
        this.ensurePresencialOfficeSelection();
      }
      this.stepValid = false;
      this.existingAppointmentInfo = null;
      this.goToStep(
        service.meetingType === "both" ? "meetingType" : "datetime"
      );
    },
    onServicesLoaded(services) {
      this.$emit("services-loaded", services);
      if (this.activationToken && this.servicePriceId && services.length > 0) {
        const service = services.find(
          (s) => Number(s.id) === Number(this.servicePriceId)
        );
        if (service) {
          this.onServiceSelected(service);
        }
      }
    },
    async nextStep() {
      if (!this.stepValid) return;
      if (
        this.currentStepKey === "meetingType" &&
        this.wizardData.meetingTypeChoice === "on-site"
      ) {
        await this.loadClientOffice();
        this.ensurePresencialOfficeSelection();
      }
      this.stepValid = false;
      this.currentStep++;
    },
    prevStep() {
      if (this.currentStep <= 1) return;
      if (this.activationToken && this.currentStep === 2) return;
      this.stepValid = true;
      this.currentStep--;
    },
    async submitAppointment() {
      if (!this.stepValid || this.isSubmitting) return;
      if (!this.effectiveAppointmentMode) {
        this.$root.toastError("error");
        return;
      }
      this.isSubmitting = true;

      if (this.effectiveAppointmentMode === "on-site") {
        await this.loadClientOffice();
        if (this.wizardData.presencialOfficeId == null) {
          this.ensurePresencialOfficeSelection();
        }
      }

      if (
        this.effectiveAppointmentMode === "on-site" &&
        this.wizardData.presencialOfficeId == null
      ) {
        this.$root.toastError("office_id_is_required");
        this.isSubmitting = false;
        return;
      }

      const payload = {
        servicePriceId: this.wizardData.selectedService.id,
        startDate: this.wizardData.dateTime.startDateUTC,
        type: this.effectiveAppointmentMode,
        userIds: [],
        notes: this.wizardData.notes || null,
        paymentMethod: this.wizardData.paymentMethod,
        origin: "Área de Cliente",
      };
      if (
        this.effectiveAppointmentMode === "on-site" &&
        this.wizardData.presencialOfficeId != null
      ) {
        payload.officeId = Number(this.wizardData.presencialOfficeId);
      }

      try {
        this.existingAppointmentInfo = null;
        const res = await this.$root.apiPost(
          this.activationToken
            ? this.$root.getApiAppointmentsCreate(this.activationToken)
            : this.$root.api.appointmentsCreate,
          payload
        );
        const link = res && res.data && res.data.paymentLink;
        this.wizardData.paymentLink =
          typeof link === "string" && link.length > 0 ? link : null;
        this.currentStep = this.wizardSteps.length;
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        const data =
          error && error.response && error.response.data
            ? error.response.data
            : null;
        const msgCode = data && data.msg_code ? data.msg_code : "error";
        if (msgCode === "appointment_already_exists") {
          const ids = data && Array.isArray(data.ids) ? data.ids : [];
          const existingId = ids.length > 0 ? Number(ids[0]) : null;
          if (existingId != null) {
            await this.loadExistingAppointmentInfo(existingId);
          }
        }
        this.$root.toastError(msgCode);
      } finally {
        this.isSubmitting = false;
      }
    },
    formatExistingAppointmentType(rawType) {
      if (rawType === "online") return "Online";
      if (rawType === "on-site") return "Presencial";
      if (rawType === "hybrid") return "Online ou Presencial";
      return rawType || "-";
    },
    async loadExistingAppointmentInfo(appointmentId) {
      try {
        const response = await this.$root.apiGet(this.$root.api.appointments, {
          params: { dataType: "list" },
        });
        const list =
          response && response.data && Array.isArray(response.data.data)
            ? response.data.data
            : [];
        const existing = list.find(function (a) {
          return Number(a.id || a.appointmentId) === Number(appointmentId);
        });
        if (!existing) return;
        const startDate = existing.startDate
          ? dayjs.utc(existing.startDate).local().format("DD/MM/YYYY HH:mm")
          : "-";
        this.existingAppointmentInfo = {
          id: appointmentId,
          dateTimeLabel: startDate,
          consultantLabel:
            existing.consultantName && String(existing.consultantName).trim()
              ? existing.consultantName
              : "-",
          typeLabel: this.formatExistingAppointmentType(
            existing.appointmentType || existing.type
          ),
        };
      } catch {
        this.existingAppointmentInfo = null;
      }
    },
  },
};
</script>
